import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "marketingConsent";

export type ConsentChoice = "accepted" | "declined";

/**
 * Read the stored consent choice from localStorage.
 * Returns null if no choice has been made yet or if localStorage is unavailable.
 */
export function getStoredConsent(): ConsentChoice | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "accepted" || v === "declined") return v;
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist a consent choice to localStorage.
 * @param choice - "accepted" or "declined"
 * @param onError - optional callback invoked when localStorage throws (e.g. private browsing)
 */
export function setStoredConsent(
  choice: ConsentChoice,
  onError?: (err: unknown) => void,
) {
  try {
    localStorage.setItem(STORAGE_KEY, choice);
  } catch (err) {
    onError?.(err);
  }
}

export interface CookieConsentProps {
  /**
   * Controls whether the banner is eligible to appear.
   * Typically set to true once the page has finished hydrating.
   */
  show: boolean;
  /** Called when the user accepts cookies. */
  onAccept: () => void;
  /** Called when the user declines cookies (or dismisses the banner). */
  onDecline: () => void;
  /**
   * URL of the privacy policy page.
   * Displayed as a link inside the banner text.
   * Defaults to "/privacy".
   */
  privacyPolicyHref?: string;
  /**
   * Link text for the privacy policy.
   * Defaults to "Privacy Policy".
   */
  privacyPolicyLabel?: string;
  /**
   * The analytics cookie description shown in the banner body.
   * Override to match your analytics stack.
   * Defaults to describing Google Analytics.
   */
  cookieDescription?: string;
  /**
   * Optional error handler for localStorage failures (e.g. private browsing mode).
   * When omitted, storage errors are silently swallowed.
   */
  onStorageError?: (err: unknown) => void;
}

/**
 * CookieConsent
 *
 * A bottom-anchored, animated consent banner styled for the dark aesthetic.
 * The banner appears after a 1.2 s delay (to avoid layout thrash on load)
 * and only if no previous consent decision is stored.
 *
 * Consent is persisted in localStorage under the "marketingConsent" key.
 * The component is entirely dependency-free — no router or error-tracking imports.
 *
 * @example
 * <CookieConsent
 *   show={pageReady}
 *   onAccept={() => enableAnalytics()}
 *   onDecline={() => disableAnalytics()}
 *   privacyPolicyHref="/privacy"
 * />
 */
export default function CookieConsent({
  show,
  onAccept,
  onDecline,
  privacyPolicyHref = "/privacy",
  privacyPolicyLabel = "Privacy Policy",
  cookieDescription = "We use analytics cookies (Google Analytics) to understand how visitors use this site so we can make it better. If marketing tools are active, they'll only run with your permission. No personal data is sold.",
  onStorageError,
}: CookieConsentProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!show) return;
    if (getStoredConsent() !== null) return;
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [show]);

  const handleAccept = () => {
    setStoredConsent("accepted", onStorageError);
    setVisible(false);
    onAccept();
  };

  const handleDecline = () => {
    setStoredConsent("declined", onStorageError);
    setVisible(false);
    onDecline();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="cookie-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          className="fixed bottom-6 left-1/2 z-[9999] w-full max-w-2xl -translate-x-1/2 px-4"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-consent-banner"
        >
          <div className="relative rounded-2xl border border-white/10 bg-black/90 backdrop-blur-md px-6 py-5 shadow-2xl shadow-black/60">
            <button
              onClick={handleDecline}
              aria-label="Dismiss"
              className="absolute right-4 top-4 text-white/30 hover:text-white/70 transition-colors"
              data-testid="button-cookie-dismiss"
            >
              <X size={16} />
            </button>

            <div className="flex items-start gap-4">
              <span className="text-2xl leading-none mt-0.5 shrink-0" aria-hidden="true">
                🍪
              </span>

              <div className="flex-1 min-w-0">
                <p className="font-subheading text-xs uppercase tracking-[0.2em] text-accent mb-1">
                  Cookie Notice
                </p>
                <h2 className="font-heading text-white text-base leading-snug mb-2">
                  We use cookies to improve your experience.
                </h2>
                <p className="text-xs text-white/50 leading-relaxed mb-4">
                  {cookieDescription}{" "}
                  <a
                    href={privacyPolicyHref}
                    className="text-accent/80 underline underline-offset-2 hover:text-accent transition-colors"
                  >
                    {privacyPolicyLabel}
                  </a>
                </p>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleAccept}
                    className="inline-flex items-center justify-center bg-accent hover:bg-accent/80 active:scale-[0.97] text-white uppercase tracking-widest text-xs font-bold font-subheading px-6 py-2.5 rounded-full transition-all"
                    data-testid="button-cookie-accept"
                  >
                    Accept
                  </button>
                  <button
                    onClick={handleDecline}
                    className="inline-flex items-center justify-center border border-white/20 hover:border-white/40 text-white/50 hover:text-white/80 active:scale-[0.97] uppercase tracking-widest text-xs font-semibold font-subheading px-6 py-2.5 rounded-full transition-all"
                    data-testid="button-cookie-decline"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
